// umlaut-check: skip — Variablen-/Funktionsnamen sind technische
// Bezeichner; sichtbare Texte tragen Umlaute korrekt.

import React, {useCallback, useEffect, useState} from "react";
import {Modal, OverlayTrigger, Table, Tooltip} from "react-bootstrap";
import axios from "axios";
import HelpTooltip from "../HelpTooltip.jsx";
import {formatMenge} from "./format.js";

const STATUS_LABEL = {vorschau: "geprüft, nicht übernommen", uebernommen: "übernommen"};
const STATUS_BADGE = {vorschau: "secondary", uebernommen: "success"};

/** Spaltenkopf mit Erklär-Tooltip (KONVENTIONEN.md Abschnitt 4/5: "Erklär-Tooltip an jedem Element"). */
function ImporteKopf({id, help, children}) {
    return (
        <th>
            <OverlayTrigger placement="top" overlay={<Tooltip id={`${id}-th-tip`}>{help}</Tooltip>}>
                <span style={{cursor: "help", borderBottom: "1px dotted #999"}}>{children}</span>
            </OverlayTrigger>
        </th>
    );
}

/** "TT.MM.JJ HH:MM" ohne das Komma, das "toLocaleString('de-DE', …)" einfügt (Sichtprüfung 1, Befund 24). */
function formatDatum(iso) {
    if (!iso) {
        return "–";
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
        return "–";
    }
    const zweistellig = (n) => String(n).padStart(2, "0");
    return `${zweistellig(d.getDate())}.${zweistellig(d.getMonth() + 1)}.${String(d.getFullYear()).slice(2)} ${zweistellig(d.getHours())}:${zweistellig(d.getMinutes())}`;
}

/**
 * Protokoll der Datei-Importe (AP6, PLAN.md Abschnitt 6): Datum, Datei,
 * Benutzer, Zähler, Status - „Rückgängig“ nur beim jüngsten, noch nicht
 * rückgängig gemachten Import, mit eigenem Bestätigungs-Modal statt
 * `confirm()` (KONVENTIONEN.md Abschnitt 4).
 */
export default function ImporteListe({neuLadenSignal}) {
    const [importe, setImporte] = useState(null);
    const [fehler, setFehler] = useState(null);
    const [rueckgaengigZiel, setRueckgaengigZiel] = useState(null);
    const [laeuft, setLaeuft] = useState(false);

    const laden = useCallback(() => {
        axios
            .get("/cosmos/daten/importe")
            .then(({data}) => setImporte(data.importe))
            .catch((e) => setFehler(e.response?.data?.message || "Die Importe konnten nicht geladen werden."));
    }, []);

    useEffect(() => {
        laden();
    }, [laden, neuLadenSignal]);

    const rueckgaengigMachen = () => {
        if (!rueckgaengigZiel) {
            return;
        }
        setLaeuft(true);
        axios
            .post(`/cosmos/daten/importe/${rueckgaengigZiel.id}/rueckgaengig`)
            .then(() => {
                setRueckgaengigZiel(null);
                laden();
            })
            .catch((e) => setFehler(e.response?.data?.message || "Rückgängig machen ist fehlgeschlagen."))
            .finally(() => setLaeuft(false));
    };

    if (importe === null || importe.length === 0) {
        return null;
    }

    return (
        <div className="mt-4">
            <h2 className="h6 d-flex align-items-center gap-2">
                Importe
                <HelpTooltip placement="right" size={0.8}>
                    Jede hochgeladene und übernommene Datei steht hier mit ihren Zählern. Nur der jüngste, noch nicht
                    rückgängig gemachte Import lässt sich zurücknehmen.
                </HelpTooltip>
            </h2>
            {fehler && <div className="alert alert-danger py-2 small">{fehler}</div>}
            <Table hover size="sm" responsive className="bg-white align-middle">
                <thead className="table-light">
                    <tr>
                        <ImporteKopf id="datum" help="Zeitpunkt der Übernahme (nicht der Zeitpunkt des Hochladens).">Datum</ImporteKopf>
                        <ImporteKopf id="datei" help="Name der hochgeladenen Datei.">Datei</ImporteKopf>
                        <ImporteKopf id="benutzer" help="Wer den Import übernommen hat.">Benutzer</ImporteKopf>
                        <ImporteKopf id="status" help="Geprüft und noch nicht übernommen, übernommen, oder wieder rückgängig gemacht.">Status</ImporteKopf>
                        <ImporteKopf id="lieferungen-neu" help="Anzahl der durch diesen Import neu entstandenen Lieferungen.">Lieferungen neu</ImporteKopf>
                        <ImporteKopf id="positionen-neu" help="Anzahl der durch diesen Import neu entstandenen Positionen (Farbtöne).">Positionen neu</ImporteKopf>
                        <ImporteKopf id="aenderungen" help="Anzahl der protokollierten Änderungszeilen, die dieser Import ausgelöst hat.">Änderungen</ImporteKopf>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {importe.map((imp) => (
                        <tr key={imp.id}>
                            <td className="small text-muted">{formatDatum(imp.importiert_am)}</td>
                            {/* #9/N5 (Sichtprüfung 2, 22.09.2026): Dateiname gedeckelt und mit
                                Auslassungspunkten statt frei wachsend - sonst sprengt ein langer
                                Dateiname die Liste über 1.104 px hinaus; voller Name im Tooltip. */}
                            <td style={{maxWidth: 200}}>
                                {imp.dateiname ? (
                                    <OverlayTrigger placement="top" overlay={<Tooltip id={`dateiname-${imp.id}`}>{imp.dateiname}</Tooltip>}>
                                        <span className="d-inline-block text-truncate" style={{maxWidth: 200}}>
                                            {imp.dateiname}
                                        </span>
                                    </OverlayTrigger>
                                ) : (
                                    "–"
                                )}
                            </td>
                            <td style={{maxWidth: 140}} className="text-truncate">
                                {imp.benutzer || "–"}
                            </td>
                            <td>
                                <span className={`badge text-bg-${STATUS_BADGE[imp.status] || "secondary"}`}>
                                    {imp.rueckgaengig_am ? "rückgängig gemacht" : STATUS_LABEL[imp.status] || imp.status}
                                </span>
                            </td>
                            <td>{imp.lieferungen_neu !== null && imp.lieferungen_neu !== undefined ? formatMenge(imp.lieferungen_neu) : "–"}</td>
                            <td>{imp.positionen_neu !== null && imp.positionen_neu !== undefined ? formatMenge(imp.positionen_neu) : "–"}</td>
                            <td>{imp.aenderungen !== null && imp.aenderungen !== undefined ? formatMenge(imp.aenderungen) : "–"}</td>
                            <td>
                                {imp.kann_rueckgaengig_gemacht_werden && (
                                    <OverlayTrigger placement="left" overlay={<Tooltip id={`rueckgaengig-tip-${imp.id}`}>Macht diesen Import vollständig rückgängig – nur möglich, solange kein neuerer Import folgt.</Tooltip>}>
                                        <span>
                                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setRueckgaengigZiel(imp)}>
                                                Rückgängig
                                            </button>
                                        </span>
                                    </OverlayTrigger>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            <Modal show={!!rueckgaengigZiel} onHide={() => setRueckgaengigZiel(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="h6">Import rückgängig machen</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-0">
                        Soll der Import <strong>{rueckgaengigZiel?.dateiname}</strong> vom {formatDatum(rueckgaengigZiel?.importiert_am)}{" "}
                        wirklich rückgängig gemacht werden?{" "}
                        {rueckgaengigZiel?.art === "snv_bewegungen"
                            ? "Die eingelesenen SNV-Warenbewegungen werden gelöscht; die Lager-Aufteilung rechnet danach wieder mit dem vorherigen SNV-Import oder, ohne einen solchen, ohne Aufteilung."
                            : rueckgaengigZiel?.art === "bestandsliste"
                              ? "Die Zeilen dieser Cosmos-Bestandsliste werden gelöscht; der Pufferbestand zeigt danach wieder die vorherige Liste oder, ohne eine solche, nichts."
                              : "Neu angelegte Lieferungen und Positionen werden gelöscht, geänderte Mengen auf den Stand davor zurückgesetzt."}
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <OverlayTrigger placement="top" overlay={<Tooltip id="rueckgaengig-abbrechen-tip">Schließt dieses Fenster, ohne etwas zu ändern.</Tooltip>}>
                        <span>
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setRueckgaengigZiel(null)} disabled={laeuft}>
                                Abbrechen
                            </button>
                        </span>
                    </OverlayTrigger>
                    <OverlayTrigger placement="top" overlay={<Tooltip id="rueckgaengig-bestaetigen-tip">Macht die Übernahme dieses Imports vollständig rückgängig.</Tooltip>}>
                        <span>
                            <button type="button" className="btn btn-danger btn-sm" onClick={rueckgaengigMachen} disabled={laeuft}>
                                {laeuft ? "wird ausgeführt…" : "Rückgängig machen"}
                            </button>
                        </span>
                    </OverlayTrigger>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
